import { Button } from "@mui/joy";
import { type SnackbarKey } from "notistack";
import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  useAdvertiserAudiences,
  useMediaDataRoom,
} from "features/mediaDataRoom/contexts";
import {
  MediaDataRoomTab,
  MediaDataRoomVersion,
  type SeedAudienceV3,
} from "features/mediaDataRoom/models";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import { dataRoomPathBuilder, DataRoomTypeNames } from "models";
import {
  AdvertiserAudienceGeneratorEmptyState,
  AdvertiserAudienceGeneratorErrorState,
  AdvertiserAudienceGeneratorLoadingState,
  AdvertiserAudienceGeneratorStep,
  useAdvertiserAudienceGenerator,
} from "../../AdvertiserAudienceGenerator";
import {
  RemarketingAdvertiserAudienceGeneratorConfigurationStep,
  RemarketingAdvertiserAudienceGeneratorSummaryStep,
} from "../components";
import {
  type RemarketingAdvertiserAudienceGeneratorContextValue,
  RemarketingAdvertiserAudienceGeneratorProvider,
} from "../contexts/RemarketingAdvertiserAudienceGeneratorContext";

const RemarketingAdvertiserAudienceGenerator = memo(() => {
  const { dataRoomId, isDeactivated, currentUserEmail } = useMediaDataRoom();
  const [audienceName, setAudienceName] = useState<string>("");
  const { currentStep } = useAdvertiserAudienceGenerator();
  const { saveAudience, seedAudiences } = useAdvertiserAudiences(
    MediaDataRoomVersion.v3
  );
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>(undefined)[1];
  const [selectedSeedAudience, setSelectedSeedAudience] =
    useState<SeedAudienceV3 | null>(null);
  const handleSetSelectedSeedAudience = useCallback(
    (audience: SeedAudienceV3) => {
      setSelectedSeedAudience(audience);
      setAudienceName(audience?.name);
    },
    []
  );
  const [generationStep, setGenerationStep] =
    useState<
      RemarketingAdvertiserAudienceGeneratorContextValue["generationStep"]
    >(null);
  useEffect(
    () =>
      setErrorSnackbarId((currentSnackbarId) => {
        if (currentSnackbarId) {
          closeSnackbar(currentSnackbarId);
        }
        return undefined;
      }),
    [enqueueSnackbar, closeSnackbar, setErrorSnackbarId]
  );
  const generateAudience = useCallback(async () => {
    try {
      if (!selectedSeedAudience?.id) {
        enqueueSnackbar("Please select seed audience", {
          variant: "info",
        });
        return;
      }
      if (!audienceName.trim()) {
        enqueueSnackbar("Please provide a name for the audience", {
          variant: "info",
        });
        return;
      }
      setGenerationStep("pending");
      await saveAudience({
        canBeViewedByCurrentUser: true,
        createdAt: new Date().toISOString(),
        createdBy: currentUserEmail,
        doesDependOnLookalikeAudience: false,
        errorReport: null,
        id: crypto.randomUUID(),
        isStale: false,
        kind: "advertiser",
        name: audienceName,
        sharedWith: [],
        sourceRef: selectedSeedAudience.id,
        sourceRefKind: "seed",
        status: "ready",
        type: crypto.randomUUID(),
      });
      setTimeout(() => {
        const dataRoomBasePath = dataRoomPathBuilder(
          dataRoomId,
          DataRoomTypeNames.PublishedMediaInsightsDcr
        );
        navigate(`${dataRoomBasePath}/${MediaDataRoomTab.activation}`);
        enqueueSnackbar("Audience successfully created.", {
          variant: "success",
        });
      }, 1500);
    } catch (error) {
      setGenerationStep(null);
      enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(error, "Failed to generate audience")
      );
    }
  }, [
    selectedSeedAudience?.id,
    audienceName,
    saveAudience,
    enqueueSnackbar,
    currentUserEmail,
    dataRoomId,
    navigate,
  ]);
  const [retryInProgress, setRetryInProgress] = useState(false);
  const retryGeneration = useCallback(async () => {
    if (isDeactivated) return;
    setRetryInProgress(true);
    try {
      await seedAudiences.retry();
    } finally {
      setRetryInProgress(false);
    }
  }, [isDeactivated, seedAudiences]);
  const contextValue =
    useMemo<RemarketingAdvertiserAudienceGeneratorContextValue>(
      () => ({
        audienceName,
        generateAudience,
        generationStep,
        seedAudiences: seedAudiences.computeResults ?? [],
        selectedSeedAudience,
        setAudienceName,
        setGenerationStep,
        setSelectedSeedAudience: handleSetSelectedSeedAudience,
      }),
      [
        audienceName,
        generateAudience,
        generationStep,
        seedAudiences.computeResults,
        selectedSeedAudience,
        handleSetSelectedSeedAudience,
      ]
    );
  if (seedAudiences.error) {
    return (
      <AdvertiserAudienceGeneratorErrorState
        RetryButton={
          !isDeactivated && (
            <Button
              color="neutral"
              loading={retryInProgress}
              onClick={retryGeneration}
              variant="soft"
            >
              Retry
            </Button>
          )
        }
      />
    );
  }
  if (seedAudiences.loading) {
    return <AdvertiserAudienceGeneratorLoadingState />;
  }
  if (!seedAudiences.computeResults?.length) {
    return <AdvertiserAudienceGeneratorEmptyState />;
  }
  let step: ReactNode = null;
  switch (currentStep) {
    case AdvertiserAudienceGeneratorStep.CONFIGURATION:
      step = <RemarketingAdvertiserAudienceGeneratorConfigurationStep />;
      break;
    case AdvertiserAudienceGeneratorStep.SUMMARY:
      step = <RemarketingAdvertiserAudienceGeneratorSummaryStep />;
      break;
  }
  return (
    <RemarketingAdvertiserAudienceGeneratorProvider value={contextValue}>
      {step}
    </RemarketingAdvertiserAudienceGeneratorProvider>
  );
});

RemarketingAdvertiserAudienceGenerator.displayName =
  "RemarketingAdvertiserAudienceGenerator";

export default RemarketingAdvertiserAudienceGenerator;
